import * as THREE from 'three';

/**
 * Carte de normales "micro-détail" de peau générée procéduralement (pores + grain fin),
 * tuilable. Les peaux MakeHuman n'ont qu'une diffuse : ce détail fait toute la différence
 * en gros plan, pour 1 texture 512² partagée (≈1 Mo GPU).
 */
export function makeSkinDetailNormal(size = 512, seed = 7): THREE.DataTexture {
  const h = new Float32Array(size * size);
  let s = seed >>> 0;
  const rnd = () => ((s = (s * 1664525 + 1013904223) >>> 0) / 4294967296);

  // Grain fin : bruit de valeur multi-octave, tuilable.
  const octave = (cells: number, amp: number) => {
    const g = new Float32Array(cells * cells);
    for (let i = 0; i < g.length; i++) g[i] = rnd();
    for (let y = 0; y < size; y++) {
      const fy = (y / size) * cells, y0 = Math.floor(fy), ty = fy - y0;
      const sy = ty * ty * (3 - 2 * ty);
      for (let x = 0; x < size; x++) {
        const fx = (x / size) * cells, x0 = Math.floor(fx), tx = fx - x0;
        const sx = tx * tx * (3 - 2 * tx);
        const a = g[(y0 % cells) * cells + (x0 % cells)], b = g[(y0 % cells) * cells + ((x0 + 1) % cells)];
        const c = g[((y0 + 1) % cells) * cells + (x0 % cells)], d = g[((y0 + 1) % cells) * cells + ((x0 + 1) % cells)];
        h[y * size + x] += amp * ((a * (1 - sx) + b * sx) * (1 - sy) + (c * (1 - sx) + d * sx) * sy);
      }
    }
  };
  octave(16, 0.35);
  octave(64, 0.22);
  octave(128, 0.14);

  // Pores : petites dépressions gaussiennes réparties aléatoirement (tuilable par modulo).
  const pores = Math.floor(size * size * 0.012);
  for (let i = 0; i < pores; i++) {
    const cx = rnd() * size, cy = rnd() * size;
    const r = 0.9 + rnd() * 1.6, depth = 0.5 + rnd() * 0.6;
    const R = Math.ceil(r * 2.5);
    for (let dy = -R; dy <= R; dy++) for (let dx = -R; dx <= R; dx++) {
      const x = ((Math.floor(cx) + dx) % size + size) % size;
      const y = ((Math.floor(cy) + dy) % size + size) % size;
      const d2 = (dx * dx + dy * dy) / (r * r);
      h[y * size + x] -= depth * Math.exp(-d2);
    }
  }

  // Hauteur → normales (différences centrales, bords repliés).
  const data = new Uint8Array(size * size * 4);
  const k = 2.2;
  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
    const xl = h[y * size + ((x - 1 + size) % size)], xr = h[y * size + ((x + 1) % size)];
    const yu = h[((y - 1 + size) % size) * size + x], yd = h[((y + 1) % size) * size + x];
    let nx = (xl - xr) * k, ny = (yu - yd) * k, nz = 1;
    const l = Math.hypot(nx, ny, nz); nx /= l; ny /= l; nz /= l;
    const o = (y * size + x) * 4;
    data[o] = (nx * 0.5 + 0.5) * 255;
    data[o + 1] = (ny * 0.5 + 0.5) * 255;
    data[o + 2] = (nz * 0.5 + 0.5) * 255;
    // Alpha : variation de rugosité (pores un peu plus mats).
    data[o + 3] = Math.max(0, Math.min(255, 128 + h[y * size + x] * 90));
  }
  const tex = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.generateMipmaps = true;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.anisotropy = 8;
  tex.colorSpace = THREE.NoColorSpace;
  tex.needsUpdate = true;
  return tex;
}
